import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const TitleSicilFixDialog = ({ open, onOpenChange, onDone }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const run = async (apply) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/voters/title-sicil-fix?apply=${apply ? 1 : 0}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setInfo(data);
      if (apply) {
        toast.success(`${data.updated} firma güncellendi`);
        onDone?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setInfo(null); setFile(null); } onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="title-fix-dialog">
        <DialogHeader>
          <DialogTitle>Excel ile Unvan & Sicil Düzeltme</DialogTitle>
          <DialogDescription>
            Sütunlar: MEVCUT UNVAN · Üye Sicil · OLMASI GEREKEN UNVAN. Eşleştirme önce sicil no,
            sonra unvan ile yapılır. Yalnızca unvan ve boş sicil no yazılır; tahmin, sorumlu,
            durum, yetki belgesi ve notlar korunur.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setInfo(null); }} data-testid="title-fix-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="title-fix-pick">
            <Upload className="h-4 w-4 mr-2" />{file ? file.name : "Excel dosyası seç"}
          </Button>

          {info && (
            <div className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid="title-fix-summary">
              <p>{info.rows} satır · eşleşen: <b>{info.matched}</b> · değişecek: <b className="text-emerald-600">{info.will_change}</b></p>
              {info.missing?.length > 0 && (
                <p className="text-amber-600" data-testid="title-fix-missing">
                  Eşleşmeyen ({info.missing.length}): {info.missing.map((m) => `${m.company_name} (${m.sicil_no})`).join(" | ")}
                </p>
              )}
              {info.changes?.length > 0 && (
                <div className="max-h-56 overflow-auto text-xs text-muted-foreground pt-1 space-y-0.5">
                  {info.changes.map((c) => (
                    <div key={c.id}>
                      {c.new_name ? <><span className="line-through">{c.old_name}</span> → <b className="text-foreground">{c.new_name}</b></> : c.old_name}
                      {c.sicil_no && <> · sicil: <b className="text-foreground">{c.sicil_no}</b></>}
                    </div>
                  ))}
                </div>
              )}
              {info.updated > 0 && <p className="text-emerald-600 font-medium">{info.updated} firma güncellendi.</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="title-fix-cancel">Kapat</Button>
          <Button variant="secondary" disabled={busy || !file} onClick={() => run(false)} data-testid="title-fix-check">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kontrol Et"}
          </Button>
          <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={busy || !info || !info.will_change}
            onClick={() => run(true)} data-testid="title-fix-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Uygula (${info?.will_change ?? 0})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TitleSicilFixDialog;
